/**
 * QFixEngagementTile — Admin dashboard tile showing cross-tenant
 * Quarterly Fix Plan engagement. Surfaces the same signal the
 * Monday Slack OPS digest already computes, for CS leads who
 * don't live in Slack.
 *
 * Layout mirrors `WhitepaperDownloadsTile` for visual consistency:
 *   - Window selector (7d/30d/90d) in the header
 *   - Headline stats: opened / sent / open-rate / distinct senders
 *   - 4-week open-rate sparkline (inline SVG, same visual language
 *     as the home rollup's receipt strip)
 *   - Top-5 senders list with per-sender open counts + org
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Mail, TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

/** 4-week mini sparkline — same visual language as the home-rollup
 *  receipt strip so the CS team reads both surfaces at a glance. */
function MiniSparkline({ weeks, direction }) {
  if (!weeks || weeks.length < 2) return null;
  const W = 120, H = 28, PAD = 2;
  const barGap = 3;
  const barW = Math.max(4, (W - 2 * PAD - barGap * (weeks.length - 1)) / weeks.length);
  const barColor = (isLast) => {
    if (!isLast) return '#FCD34D';
    if (direction === 'up') return '#10B981';
    if (direction === 'down') return '#F43F5E';
    return '#FCD34D';
  };
  return (
    <svg width={W} height={H} data-testid="qfix-engagement-sparkline">
      {weeks.map((w, i) => {
        const x = PAD + i * (barW + barGap);
        const h = Math.max(1, (w.open_rate || 0) * (H - 2 * PAD));
        const y = H - PAD - h;
        const isLast = i === weeks.length - 1;
        return (
          <rect
            key={w.week_start}
            x={x}
            y={y}
            width={barW}
            height={h}
            fill={barColor(isLast)}
            rx={1}
          >
            <title>
              {`Week of ${w.week_start}: ${w.opened}/${w.sent} opens (${Math.round((w.open_rate || 0) * 100)}%)`}
            </title>
          </rect>
        );
      })}
    </svg>
  );
}

function TrendPill({ direction }) {
  const cfg = {
    up: { Icon: TrendingUp, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'trending up' },
    down: { Icon: TrendingDown, cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'trending down' },
    flat: { Icon: Minus, cls: 'bg-slate-50 text-slate-600 border-slate-200', label: 'flat' },
  }[direction] || { Icon: Minus, cls: 'bg-slate-50 text-slate-600 border-slate-200', label: '—' };
  const { Icon, cls, label } = cfg;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider ${cls}`}
      data-testid={`qfix-engagement-trend-${direction}`}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export default function QFixEngagementTile() {
  const [windowDays, setWindowDays] = useState(7);
  const { setViewAsOrg } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error } = useTelemetryTile({
    url: `/admin/qfix-engagement?window_days=${windowDays}&weeks=4`,
    deps: [windowDays],
  });

  // Drill-in affordance: clicking a sender row swaps the signed-in
  // super-admin into that sender's org preview and lands them on
  // the home page — where the receipt strip will then show that
  // tenant's full board-email engagement detail. One-click from
  // "who's been sending?" → "let me see their full context".
  const handleSenderClick = (sender) => {
    if (!sender?.organization_id) {
      toast.info('This sender has no org context — cannot drill in.');
      return;
    }
    setViewAsOrg(sender.organization_id, sender.org_name || sender.organization_id);
    toast.success(`Viewing as ${sender.org_name || sender.organization_id}`);
    navigate('/');
  };

  // Super-admin-only surface. Non-SA users get a silent dismiss so
  // the tile simply doesn't render on their dashboard.
  if (error === 'forbidden') return null;

  const sent = data?.total_sent ?? 0;
  const opened = data?.total_opened ?? 0;
  const rate = Math.round((data?.open_rate ?? 0) * 100);
  const senders = data?.unique_senders ?? 0;
  const topSenders = data?.top_senders || [];
  const weeks = data?.weeks || [];
  const direction = data?.trend_direction || 'flat';

  return (
    <Card
      data-testid="qfix-engagement-tile"
      className="border-rose-200 bg-gradient-to-br from-white to-rose-50/40"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-rose-500" />
              Quarterly Fix Plan Engagement
            </CardTitle>
            <CardDescription className="mt-0.5">
              Board-email opens across all tenants. Surfaced here so CS can see what the Monday Slack digest is already posting.
            </CardDescription>
          </div>
          <div
            className="inline-flex bg-white border border-slate-200 rounded-md p-0.5"
            data-testid="qfix-engagement-window-selector"
          >
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setWindowDays(o.days)}
                className={
                  'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                  (windowDays === o.days
                    ? 'bg-rose-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100')
                }
                data-testid={`qfix-engagement-window-${o.days}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="qfix-engagement-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="qfix-engagement-error">
            Couldn't load engagement telemetry.
          </p>
        ) : sent === 0 ? (
          <p className="text-xs text-slate-500" data-testid="qfix-engagement-empty">
            No Quarterly Fix Plan emails sent in the last {windowDays} days. CLOs pin plans from their home page rollup tile.
          </p>
        ) : (
          <div className="space-y-3" data-testid="qfix-engagement-data">
            {/* Headline stats row */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-3 flex-wrap">
                <div>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums leading-none">
                    <span data-testid="qfix-engagement-opened">{opened}</span>
                    <span className="text-slate-400 text-lg"> / </span>
                    <span data-testid="qfix-engagement-sent">{sent}</span>
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                    opened · sent
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-rose-700 tabular-nums leading-none">
                    <span data-testid="qfix-engagement-rate">{rate}</span>
                    <span className="text-rose-400 text-lg">%</span>
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                    open rate
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-700 tabular-nums leading-none">
                    <span data-testid="qfix-engagement-senders">{senders}</span>
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                    senders
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <MiniSparkline weeks={weeks} direction={direction} />
                <TrendPill direction={direction} />
              </div>
            </div>
            {/* Top senders — scannable list with opens/sent + org */}
            {topSenders.length > 0 && (
              <div className="pt-2 border-t border-rose-100">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                  Top senders this {windowDays === 7 ? 'week' : `${windowDays}d`}
                </p>
                <ol className="space-y-1" data-testid="qfix-engagement-top-senders">
                  {topSenders.map((s, i) => (
                    <li key={s.email} data-testid={`qfix-engagement-sender-${i}`}>
                      <button
                        type="button"
                        onClick={() => handleSenderClick(s)}
                        disabled={!s.organization_id}
                        className="group w-full flex items-center justify-between gap-2 text-[12px] text-left px-1.5 py-1 rounded hover:bg-white/80 disabled:hover:bg-transparent disabled:cursor-default transition-colors"
                        title={s.organization_id
                          ? `View ${s.org_name || s.organization_id}'s home page as a super-admin preview`
                          : 'No org context on this sender'}
                        data-testid={`qfix-engagement-sender-drill-${i}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800 truncate">{s.email}</p>
                          {s.org_name && (
                            <p className="text-[10px] text-slate-500 truncate">{s.org_name}</p>
                          )}
                        </div>
                        <span className="shrink-0 text-slate-600 tabular-nums text-[11px] flex items-center gap-1">
                          <span>
                            <span className="font-semibold text-emerald-700">{s.opened}</span>
                            {' / '}
                            {s.sent}
                          </span>
                          {s.organization_id && (
                            <ArrowRight className="h-3 w-3 text-slate-400 group-hover:text-rose-600 group-hover:translate-x-0.5 transition-all" />
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
